(() => {
  const CAROUSEL_SELECTOR = '.recipe-card-carousel';
  const CARD_SELECTOR = '.recipe-card';

  /**
   * Initialize a single recipe-card carousel.
   *
   * Mobile swipe and snapping are handled by CSS. This script adds:
   * - Active-card tracking
   * - Left/right keyboard navigation
   * - Improved accessibility state
   */
  function initializeRecipeCarousel(carousel) {
    const cards = Array.from(carousel.querySelectorAll(CARD_SELECTOR));

    if (cards.length < 2) {
      return;
    }

    carousel.setAttribute('tabindex', '0');

    let animationFrameId = null;

    const isHorizontallyScrollable = () =>
      carousel.scrollWidth > carousel.clientWidth + 1;

    const getClosestCardIndex = () => {
      let closestIndex = 0;
      let closestDistance = Infinity;

      cards.forEach((card, index) => {
        const distance = Math.abs(card.offsetLeft - carousel.scrollLeft);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    };

    const setActiveCard = (activeIndex) => {
      cards.forEach((card, index) => {
        if (index === activeIndex) {
          card.setAttribute('aria-current', 'true');
        } else {
          card.removeAttribute('aria-current');
        }
      });

      carousel.dataset.activeCard = String(activeIndex);
    };

    const updateActiveCard = () => {
      animationFrameId = null;

      if (!isHorizontallyScrollable()) {
        cards.forEach((card) => card.removeAttribute('aria-current'));
        delete carousel.dataset.activeCard;
        return;
      }

      setActiveCard(getClosestCardIndex());
    };

    const requestActiveCardUpdate = () => {
      if (animationFrameId !== null) {
        return;
      }

      animationFrameId = window.requestAnimationFrame(updateActiveCard);
    };

    const scrollToCard = (index) => {
      const targetCard = cards[index];

      if (!targetCard) {
        return;
      }

      targetCard.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    };

    carousel.addEventListener(
      'scroll',
      requestActiveCardUpdate,
      { passive: true }
    );

    carousel.addEventListener('keydown', (event) => {
      if (
        !isHorizontallyScrollable() ||
        (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')
      ) {
        return;
      }

      event.preventDefault();

      const currentIndex = Number.parseInt(
        carousel.dataset.activeCard || '0',
        10
      );

      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const nextIndex = Math.min(
        Math.max(currentIndex + direction, 0),
        cards.length - 1
      );

      scrollToCard(nextIndex);
    });

    window.addEventListener('resize', requestActiveCardUpdate);

    updateActiveCard();
  }

  function initializeAllRecipeCarousels() {
    document
      .querySelectorAll(CAROUSEL_SELECTOR)
      .forEach(initializeRecipeCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeAllRecipeCarousels
    );
  } else {
    initializeAllRecipeCarousels();
  }
})();